import React, { useState } from 'react';
import { useGame } from '../../contexts/GameContext';
import { TomeTabs } from '../../primitives/tome';
import { CharacterStatsContent } from '../../Systems/CharacterStatsUI';
import { classColor, formatCallingName } from '../../theme';
import type { CharacterTab } from './characterPresentation';
import { EquipmentContent } from './EquipmentContent';

const CHARACTER_TABS = [{ id: 'equipment', label: 'Equipment' }, { id: 'stats', label: 'Stats' }] as const;

export const CharacterContent: React.FC = () => {
  const { player } = useGame();
  const [activeTab, setActiveTab] = useState<CharacterTab>('equipment');

  const identityReady = player != null
    && typeof player.name === 'string'
    && typeof player.class === 'string'
    && Number.isFinite(player.level);
  const identityStyle = identityReady
    ? ({ '--character-class-color': classColor(player.class) } as React.CSSProperties)
    : undefined;

  return (
    <div className="character-sheet" data-testid="character-window">
      <header className="character-identity" style={identityStyle}>
        {identityReady ? (
          <>
            <div className="character-identity__mark" aria-hidden="true">
              {player.name.slice(0, 1).toUpperCase()}
            </div>
            <div className="character-identity__copy">
              <p className="character-eyebrow">Adventurer dossier</p>
              <h1>{player.name}</h1>
            </div>
            <dl className="character-identity__facts">
              <div>
                <dt>Class</dt>
                <dd>{formatCallingName(player.class)}</dd>
              </div>
              <div>
                <dt>Level</dt>
                <dd>{player.level}</dd>
              </div>
            </dl>
          </>
        ) : (
          <div className="character-identity__unavailable" role="status">
            <h1>Character unavailable</h1>
            <p>Waiting for your character to load.</p>
          </div>
        )}
      </header>

      <TomeTabs tabs={CHARACTER_TABS} active={activeTab} onSelect={setActiveTab} label="Character sections" idPrefix="character" />

      <div
        id="character-panel-equipment"
        role="tabpanel"
        aria-labelledby="character-tab-equipment"
        className="character-tabpanel"
        hidden={activeTab !== 'equipment'}
      >
        <EquipmentContent />
      </div>
      <div
        id="character-panel-stats"
        role="tabpanel"
        aria-labelledby="character-tab-stats"
        className="character-tabpanel"
        hidden={activeTab !== 'stats'}
      >
        <CharacterStatsContent />
      </div>
    </div>
  );
};
